import React, { useRef, useState, useContext } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import ExcelJS from 'exceljs';
import ExportPopover from './ExportPopover';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { followCursor } from 'tippy.js';
import { ProductsContext } from '../context/products.context';
import { shortenCipName } from '../utils/shortenCipName';

// 1) Bring in or define getStudentGroupDisplay in this file, e.g.:
const renameMap = {
  "All Students": "students across all groups",
  "X": "Gender X students",
  "Students w/ Disabilities": "Students with Disabilities",
  "English Learners": "English Language Learners",
  "Section504": "Section 504 Students",
  "Military Parent": "Students with Military Parents",
  "Homeless": "Homeless Students",
  "Two or More Races": "Students of Two or More Races"
  // Add other mappings as needed
};

function getStudentGroupDisplay(group) {
  if (renameMap[group]) {
    return renameMap[group];
  }
  // Groups that don't need "students" appended in your fallback
  const exceptions = ["All Students"];
  if (exceptions.includes(group)) {
    return group;
  }
  // Default: Append "students"
  return `${group} students`;
}



/**
 * clamp() – ensures a value stays between [minVal, maxVal].
 */
function clamp(value, minVal, maxVal) {
  return Math.max(minVal, Math.min(value, maxVal));
}

/**
 * truncate() – shortens a string if over maxLength, and appends "…".
 * E.g. truncate("InternationalEconomics", 10) => "Internation…"
 */
function truncate(str, maxLength = 12) {
  if (!str) return "";
  return str.length > maxLength
    ? str.slice(0, maxLength - 1) + "…"
    : str;
}

// A custom symbol for "Missing Students" legend item
function MissingStudentsLegendSymbol({ x, y, size }) {
  return (
    <g transform={`translate(${x}, ${y}) scale(0.7)`}>
      <path
        fill="rgba(255,0,0,0.7)"
        d="M7.5 6a4.5 4.5 0 1 1 9 0 
           4.5 4.5 0 0 1-9 0ZM3.751 20.105
           a8.25 8.25 0 0 1 16.498 0 
           .75.75 0 0 1-.437.695
           A18.683 18.683 0 0 1 12 22.5
           c-2.786 0-5.433-.608-7.812-1.7
           a.75.75 0 0 1-.437-.695Z"
      />
    </g>
  );
}

const EquityNivoBarChart = ({studentGroups, cipEquityData, showMissing, focus}) => {
  const {
    currentCip,
    cipDoc,
    selectedSchool,
    schoolDemo,
    selectedFilterIndicatorDate,
    districtName,
 
  } = useContext(ProductsContext);
  const chartRef = useRef(null);
  const [alertMessage, setAlertMessage] = useState(null); // { message: '...', type: 'success' }

    // We'll also track the chart width for fraction-based legend
    const chartWidthRef = useRef(0);
    const chartHeightRef = useRef(0);
  const triggerAlert = (message, type = 'success') => {
    setAlertMessage({ message, type });
    setTimeout(() => {
      setAlertMessage(null);
    }, 3000);
  };

    // Function to handle exporting the chart as PNG and embedding into Excel
    const exportChartToExcel = async () => {
      if (chartRef.current === null) {
        throw new Error('Chart is not available for export.');
      }
  
      // Find the SVG element within the container
      const svg = chartRef.current.querySelector('svg');
      if (!svg) {
        throw new Error('SVG element not found.');
      }
  
      try {
        // Serialize the SVG element to a string
        const serializer = new XMLSerializer();
        let source = serializer.serializeToString(svg);
  
        // Add namespaces if they are missing
        if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
          source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        if (!source.match(/^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/)) {
          source = source.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
        }
  
        // Add XML declaration
        source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
  
        // Create a Blob from the SVG source
        const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
  
        // Create an image to load the SVG
        const img = new Image();
        img.crossOrigin = 'anonymous'; // To avoid CORS issues
        img.onload = async () => {
          try {
            // Define scaling factor for high-DPI (Retina) displays
            const scale = 2; // Adjust as needed
            const canvas = document.createElement('canvas');
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const context = canvas.getContext('2d');
  
            // Scale the context to support high-DPI
            context.scale(scale, scale);
            context.drawImage(img, 0, 0);
  
            // Create a PNG data URL from the canvas
            const pngData = canvas.toDataURL('image/png');
  
            // Convert the PNG data URL to a Uint8Array
            const response = await fetch(pngData);
            const blob = await response.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
  
            // Create a new Excel workbook and worksheet
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Equity Chart');
  
            // Add the chart image to the workbook
            const imageId = workbook.addImage({
              buffer: uint8Array,
              extension: 'png',
            });
  
            // Define where to place the image (e.g., A1)
            worksheet.addImage(imageId, {
              tl: { col: 0, row: 0 },
              ext: { width: 960, height: 500 }, // Adjust size as needed
            });
  

            // Add the FERPA caption
            const captionRow = 27;
            // worksheet.mergeCells(`A${captionRow}:G${captionRow}`);
            // worksheet.getCell(`A${captionRow}`).value = `The following groups were suppressed in accordance with FERPA: ${suppressedGroups.join(', ')}`;
            // worksheet.getCell(`A${captionRow}`).font = { italic: true };
            // worksheet.getCell(`A${captionRow}`).alignment = { wrapText: true };
  
            // Add the underlying data below the chart
            const startRow = captionRow + 2;
            const headers = ['Demographic', 'CIP Numerator', 'CIP Denominator', 'CIP %', 'School Numerator', 'School Denominator', 'Overall %'];
            headers.forEach((header, index) => {
              const cell = worksheet.getCell(`${String.fromCharCode(65 + index)}${startRow}`);
              cell.value = header;
              cell.font = { bold: true };
            });
  
            // Populate the data
            chartData.forEach((data, rowIndex) => {
              const rowNumber = startRow + 1 + rowIndex;
              worksheet.getCell(`A${rowNumber}`).value = data.studentGroup;
              worksheet.getCell(`B${rowNumber}`).value = data.cipCount;
              worksheet.getCell(`C${rowNumber}`).value = data.cipAll;
              worksheet.getCell(`D${rowNumber}`).value = data['CIP %'] / 100;
              worksheet.getCell(`E${rowNumber}`).value = data.schoolCount;
              worksheet.getCell(`F${rowNumber}`).value = data.schoolAll;
              worksheet.getCell(`G${rowNumber}`).value = data['Overall %'] / 100;
            });
  
            // Format percentage columns
            worksheet.getColumn('D').numFmt = '0.0%';
            worksheet.getColumn('G').numFmt = '0.0%';
  
            // Adjust column widths for better readability
            worksheet.getColumn('A').width = 30;
            ['B', 'C', 'E', 'F'].forEach((col) => {
              worksheet.getColumn(col).width = 15;
            });
            worksheet.getColumn('D').width = 15;
            worksheet.getColumn('G').width = 15;
  
            // Write the workbook to a buffer
            const excelBuffer = await workbook.xlsx.writeBuffer();
  
            // Create a Blob from the buffer
            const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  
            // Trigger download of the Excel file
            const excelUrl = URL.createObjectURL(excelBlob);
            const downloadLink = document.createElement('a');
            downloadLink.href = excelUrl;
            // Helper to remove spaces / special chars from filenames
const sanitizeForFilename = (str) =>
  str.replace(/\s+/g, '_').replace(/[^\w.\-_]/g, '');


// Map your "focus" strings to a short label
const focusMap = {
  'Gender & Special Student Groups': 'GndrSp',
  'Race/Ethnicity': 'RacEth',
};
/**
 * Removes "High School" or "Middle School" from a name, case-insensitively.
 * Trims extra spaces afterward.
 */
function cleanupSchoolName(name) {
  if (!name) return '';

  return name
    .replace(/high school/i, '')
    .replace(/middle school/i, '')
    .trim();
}

// 1) Gather your raw info
const cipCode = cipDoc.cipDetails?.cipCode ?? currentCip; 
const rawCipName = cipDoc.cipDetails?.cipName ?? currentCip ?? 'UnknownCIP';
const rawFocusLabel = focusMap[focus] ?? 'Demo';

let schoolOrDistrict =
  selectedSchool.value === 'District Total'
    ? 'DistrictTotal'
    : selectedSchool.value;

// 2) Remove "High School" or "Middle School"
schoolOrDistrict = cleanupSchoolName(schoolOrDistrict);

// 3) Get a short CIP name using your new utility
const shortenedCipName = shortenCipName(cipCode, rawCipName, 30);

// 4) Optionally truncate the school name if you like (30 chars)
const truncatedSchool = truncate(schoolOrDistrict, 30); // assuming you have a truncate() imported or defined

// 5) Sanitize each segment
const safeFocus = sanitizeForFilename(rawFocusLabel);
const safeCipName = sanitizeForFilename(shortenedCipName);
const safeSchool = sanitizeForFilename(truncatedSchool);
const safeYear = sanitizeForFilename(selectedFilterIndicatorDate.label || 'UnknownYear');

// 6) Build the filename array, removing any empty segments
const segments = [safeFocus, safeCipName, safeSchool, safeYear].filter(Boolean);
let finalName = segments.join('_');

// 7) Replace multiple underscores with a single underscore
finalName = finalName.replace(/_+/g, '_');

// 8) Assign final download filename
downloadLink.download = `${finalName}.xlsx`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
  
            // Release the object URLs
            URL.revokeObjectURL(url);
            URL.revokeObjectURL(excelUrl);
  
            // No need to trigger alert; ExportButton handles it
          } catch (error) {
            console.error('Error embedding image into Excel:', error);
            throw new Error('An error occurred while embedding the chart into Excel.');
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          throw new Error('Failed to load the chart image for export. Ensure you are using a modern version of Chrome or Edge.');
        };
        img.src = url;
      } catch (error) {
        console.error('Error exporting chart to Excel:', error);
        throw new Error('An error occurred while exporting the chart to Excel.');
      }
    };
  
    // Function to handle exporting the chart as PNG
    const exportChartAsPNG = () => {
      if (chartRef.current === null) {
        triggerAlert('Chart is not available for export.', 'error');
        return;
      }
  
        // Find the SVG element within the container
  const svg = chartRef.current.querySelector('svg');
  if (!svg) {
    triggerAlert('SVG element not found.', 'error');
    return;
  }

  try {
    // ---------------------------
    // 1) Reuse your existing naming logic
    // ---------------------------
    
    // Helper to remove spaces / special chars from filenames
    const sanitizeForFilename = (str) =>
      str.replace(/\s+/g, '_').replace(/[^\w.\-_]/g, '');

    // Map your "focus" strings to a short label
    const focusMap = {
      'Gender & Special Student Groups': 'GndrSp',
      'Race/Ethnicity': 'RacEth',
    };
    /**
     * Removes "High School" or "Middle School" from a name, case-insensitively.
     * Trims extra spaces afterward.
     */
    function cleanupSchoolName(name) {
      if (!name) return '';
      return name
        .replace(/high school/i, '')
        .replace(/middle school/i, '')
        .trim();
    }

    // 1) Gather your raw info
    const cipCode = cipDoc.cipDetails?.cipCode ?? currentCip;
    const rawCipName = cipDoc.cipDetails?.cipName ?? currentCip ?? 'UnknownCIP';
    const rawFocusLabel = focusMap[focus] ?? 'Demo';

    let schoolOrDistrict =
      selectedSchool.value === 'District Total'
        ? 'DistrictTotal'
        : selectedSchool.value;

    // 2) Remove "High School" or "Middle School"
    schoolOrDistrict = cleanupSchoolName(schoolOrDistrict);

    // 3) Get a short CIP name using your utility
    const shortenedCipName = shortenCipName(cipCode, rawCipName, 30);

    // 4) Truncate the school name if you like
    const truncatedSchool = truncate(schoolOrDistrict, 30);

    // 5) Sanitize each segment
    const safeFocus = sanitizeForFilename(rawFocusLabel);
    const safeCipName = sanitizeForFilename(shortenedCipName);
    const safeSchool = sanitizeForFilename(truncatedSchool);
    const safeYear = sanitizeForFilename(selectedFilterIndicatorDate.label || 'UnknownYear');

    // 6) Build the filename array, removing any empty segments
    const segments = [safeFocus, safeCipName, safeSchool, safeYear].filter(Boolean);
    let finalName = segments.join('_');

    // 7) Replace multiple underscores with a single underscore
    finalName = finalName.replace(/_+/g, '_');

    const finalFileName = `${finalName}.png`;

    // ---------------------------
    // 2) Continue with your PNG export logic
    // ---------------------------

    // Serialize the SVG element to a string
    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(svg);

    // Add namespaces if missing
    if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
      source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
    }
    if (!source.match(/^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/)) {
      source = source.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
    }

    // Add XML declaration
    source = '<?xml version="1.0" standalone="no"?>\r\n' + source;

    // Create a Blob from the SVG source
    const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    // Create an image to load the SVG
    const img = new Image();
    img.crossOrigin = 'anonymous'; // To avoid CORS issues
    img.onload = () => {
      try {
        // Define scaling factor for high-DPI (Retina) displays
        const scale = 2; // Adjust as needed
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const context = canvas.getContext('2d');

        // Scale the context to support high-DPI
        context.scale(scale, scale);
        context.drawImage(img, 0, 0);

        // Create a PNG data URL from the canvas
        const pngData = canvas.toDataURL('image/png');

        // Trigger download of the PNG file
        const downloadLink = document.createElement('a');
        downloadLink.href = pngData;
        downloadLink.download = finalFileName; // <-- Use our dynamic filename
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Release the object URL
        URL.revokeObjectURL(url);

        triggerAlert('Chart exported as PNG successfully!', 'success');
      } catch (error) {
        console.error('Error exporting chart as PNG:', error);
        triggerAlert('An error occurred while exporting the chart as PNG.', 'error');
      }
    };

    img.onerror = () => {
      triggerAlert('Failed to load the chart image for export.', 'error');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  } catch (error) {
    console.error('Error exporting chart as PNG:', error);
    triggerAlert('An error occurred while exporting the chart as PNG.', 'error');
  }
}
  
    const copyImageToClipboard = async () => {
      if (chartRef.current === null) {
        throw new Error('Chart is not available for copying.');
      }
    
      // Check if ClipboardItem is supported
      if (!('ClipboardItem' in window)) {
        throw new Error('ClipboardItem is not supported in this browser.');
      }
    
      // Find the SVG element within the container
      const svg = chartRef.current.querySelector('svg');
      if (!svg) {
        throw new Error('SVG element not found.');
      }
    
      try {
        // Serialize the SVG element to a string
        const serializer = new XMLSerializer();
        let source = serializer.serializeToString(svg);
    
        // Add namespaces if they are missing
        if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
          source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        if (!source.match(/^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/)) {
          source = source.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
        }
    
        // Add XML declaration
        source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
    
        // Create a Blob from the SVG source
        const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
    
        // Create an image to load the SVG
        const img = new Image();
        img.crossOrigin = 'anonymous'; // To avoid CORS issues
    
        // Wrap the onload logic in a Promise
        await new Promise((resolve, reject) => {
          img.onload = async () => {
            try {
              // Define scaling factor for high-DPI (Retina) displays
              const scale = 2; // Adjust as needed
              const canvas = document.createElement('canvas');
              canvas.width = img.width * scale;
              canvas.height = img.height * scale;
              const context = canvas.getContext('2d');
    
              // Scale the context to support high-DPI
              context.scale(scale, scale);
              context.drawImage(img, 0, 0);
    
              // Convert the canvas to a Blob using a Promise
              const blob = await new Promise((resolveBlob, rejectBlob) => {
                canvas.toBlob((blob) => {
                  if (blob) {
                    resolveBlob(blob);
                  } else {
                    rejectBlob(new Error('Failed to generate image blob.'));
                  }
                }, 'image/png');
              });
    
              // Create a ClipboardItem
              const clipboardItem = new window.ClipboardItem({ 'image/png': blob });
    
              // Write to the clipboard
              await navigator.clipboard.write([clipboardItem]);
    
              // Resolve the outer Promise
              resolve();
    
              // Release the object URL
              URL.revokeObjectURL(url);
            } catch (error) {
              console.error('Error copying chart image to clipboard:', error);
              // Release the object URL
              URL.revokeObjectURL(url);
              reject(error);
            }
          };
    
          img.onerror = () => {
            console.error('Failed to load the chart image for copying.');
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load the chart image for copying.'));
          };
    
          img.src = url;
        });
      } catch (error) {
        console.error('Error copying chart image to clipboard:', error);
        throw error;
      }
    };
    
    // Function to copy data to clipboard
    const copyDataToClipboard = async () => {
      try {
        // Prepare data as tab-separated values
        const headers = ['Demographic', 'CIP Numerator', 'CIP Denominator', 'CIP %', 'School Numerator', 'School Denominator', 'Overall %'];
        const rows = chartData
          .filter(data => data['CIP %'] != null && data['Overall %'] != null)
          .map(data => [
            data.studentGroup,
            data.cipCount,
            data.cipAll,
            data['CIP %'].toFixed(1) + '%',
            data.schoolCount,
            data.schoolAll,
            data['Overall %'].toFixed(1) + '%',
          ]);
  
        const tableText =
          [headers, ...rows]
            .map(e => e.join('\t'))
            .join('\n');
  
        // Copy to clipboard
        await navigator.clipboard.writeText(tableText);
        // No need to trigger alert; ExportButton handles it
      } catch (err) {
        console.error('Failed to copy data to clipboard:', err);
        throw err; // Re-throw the error to be caught by the ExportButton component
      }
    };

  // Initialize variables
  let chartData = [];
  let suppressedGroups = [];
  let hasChartData = false;
  let titleText = '';


// e.g. "Business & Marketing" or fallback to currentCip
const cipName = cipDoc.cipDetails?.cipName || currentCip;
const schOrDist = selectedSchool.value === 'District Total' ? "District" : "School"

// Sub-title: e.g. "By Gender & Special Student Groups, 2023–24"
const subTitle = `By ${focus}`;


  // Check if cipEquityData and schoolDemo are available
  if (cipEquityData && schoolDemo) {
    // Find the CIP data for the current CIP code
    const cipData = cipEquityData.find((cip) => cip.cipcode === currentCip);

    if (cipData) {
      // Get the list of schools that have enrollment data for the current CIP
      const schoolsWithData = cipData.enrollment.map((e) => e.schoolName);

      // Get selected school(s)
      const selectedSchools =
        selectedSchool.value === 'District Total'
          ? schoolsWithData
          : [selectedSchool.value];

      // Check if selected school(s) have data
      const hasData = selectedSchools.some((schoolName) =>
        schoolsWithData.includes(schoolName)
      );

      // Define the year index based on selectedFilterIndicatorDate
      const yearIndex = Number(selectedFilterIndicatorDate.value) - 2021;

      

      // Prepare chart data with the new filtering logic
      chartData = [];
      suppressedGroups = [];

      // Process each student group
      studentGroups.forEach((group) => {
        let cipNumerator = 0;
        let cipDenominator = 0;
        let schoolNumerator = 0;
        let schoolDenominator = 0;

        // Process each selected school
        selectedSchools.forEach((schoolName) => {
          // Find the CIP enrollment data for the school
          const schoolEnrollmentData = cipData.enrollment.find(
            (e) => e.schoolName === schoolName
          );

          // Find the school demo data for the school
          const schoolDemoData = schoolDemo.find(
            (s) => s.schoolName === schoolName
          );

          if (schoolEnrollmentData && schoolDemoData) {
            // Get the CIP Numerator and Denominator
            const cipGroupCounts = schoolEnrollmentData[group];
            const cipAllCounts = schoolEnrollmentData['All'];

            if (cipGroupCounts && cipAllCounts) {
              const cipGroupCount = cipGroupCounts[yearIndex];
              const cipAllCount = cipAllCounts[yearIndex];

              if (
                cipGroupCount !== undefined &&
                cipAllCount !== undefined &&
                cipGroupCount !== null &&
                cipAllCount !== null
              ) {
                cipNumerator += cipGroupCount;
                cipDenominator += cipAllCount;
              }
            }

            // Get the School Numerator and Denominator
            const schoolGroupCounts = schoolDemoData[group];
            const schoolAllCounts = schoolDemoData['All'];

            if (schoolGroupCounts && schoolAllCounts) {
              const schoolGroupCount = schoolGroupCounts[yearIndex];
              const schoolAllCount = schoolAllCounts[yearIndex];

              if (
                schoolGroupCount !== undefined &&
                schoolAllCount !== undefined &&
                schoolGroupCount !== null &&
                schoolAllCount !== null
              ) {
                schoolNumerator += schoolGroupCount;
                schoolDenominator += schoolAllCount;
              }
            }
          }
        });

        // Calculate percentages
        let cipPercentage =
          cipDenominator > 0 ? (cipNumerator / cipDenominator) * 100 : null;
        let schoolPercentage =
          schoolDenominator > 0 ? (schoolNumerator / schoolDenominator) * 100 : null;

        // Apply the new filtering logic
        if (
          (cipPercentage === null || cipPercentage < 2) &&
          (schoolPercentage === null || schoolPercentage < 2)
        ) {
          suppressedGroups.push({
            studentGroup: group,
            'CIP %': cipPercentage,
            'Overall %': schoolPercentage,
          });
          return;
        }

        // Include data point in chartData
        chartData.push({
          studentGroup: group,
          cipCount: cipNumerator,
          cipAll: cipDenominator,
          'CIP %': cipPercentage,
          schoolCount: schoolNumerator,
          schoolAll: schoolDenominator,
          'Overall %': schoolPercentage,
        });
      });

      // Check if there's chart data to display
      hasChartData = chartData.length > 0;

      // Ensure at least 5 student groups by padding with unique empty data
      const minimumGroups = 5;
      if (chartData.length < minimumGroups) {
        const numberOfPadding = minimumGroups - chartData.length;
        for (let i = 1; i <= numberOfPadding; i++) {
          chartData.push({
            studentGroup: `filler${i}`, // Unique name for each padding group
            cipCount: null,
            cipAll: null,
            'CIP %': null,
            schoolCount: null,
            schoolAll: null,
            'Overall %': null,
          });
        }
      }

      // Define the title
      titleText = `${cipName} Enrollment at ${
        selectedSchool.value === 'District Total' ? districtName + " " : selectedSchool.value
      } Compared to Overall ${selectedSchool.value === 'District Total' ? "District" : "School"} Enrollment, ${selectedFilterIndicatorDate.label}`;
    }
  }

  // Define the keys for the bar chart
  const keys = ['CIP %', 'Overall %'];

  // Define custom colors for the bars
  const colors = {
    'CIP %': '#A3CAFA',
    'Overall %': '#E1E3E5', 
  };

  // Utility function to format percentages
  const formatPercent = (value) => {
    if (typeof value === 'number' && !isNaN(value)) {
      return `${value.toFixed(0)}%`;
    }
    return null;
  };

  // Function to split text into chunks of N characters while respecting word boundaries
  const splitTextOnWords = (text, maxLength) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach((word) => {
      if ((currentLine + word).length > maxLength) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine += (currentLine ? ' ' : '') + word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  };


  // 3) We'll define a small custom layer that captures chart width
  const captureWidthLayer = ({ width,height }) => {
    chartWidthRef.current = width;
    chartHeightRef.current = height;
    return null; // no rendering
  };

  const dynamicLegendLayer = ({
    width, // total chart width
    height,
    showMissing,
    cipDoc,
    districtName,
    selectedSchool,
    MissingStudentsLegendSymbol, 
    colors,
  }) => {
    // (1) Decide how wide the "legend row" is
    const legendWidth = clamp(width * 0.9, 0, width - 50); 
    // For instance, 80% of total chart width, but never exceed (width - 50)
  
    // (2) Each chunk is legendWidth / 3
    const chunkWidth = legendWidth / 3;
  
    // (3) We'll center the entire legend row horizontally by offsetting half the difference
    const xOffset = (width - legendWidth) / 2;
    // We'll place it e.g. 20 px from the top
    const yOffset = -50;
  

    const CIPLabel = `CIP %`;
  
    // (5) Build the School label
    // if District total => `${districtName} Demographic`, else e.g. "HighSchool Demographic"
    let rawSchoolName =
      selectedSchool.value === "District Total"
        ? districtName
        : selectedSchool.value;
    const truncatedSchool = truncate(rawSchoolName, 15);
    const SchoolLabel = `Overall %`;
  
    // We'll place CIP in chunk 0, School in chunk 1
    // If showMissing => place missing in chunk 2
  
    // CIP item metadata
    const CIPItem = {
      x: 0,
      label: CIPLabel,
      color: colors["CIP %"],
      shape: "circle", // or a custom symbol
    };
  
    const SchoolItem = {
      x: 1,
      label: SchoolLabel,
      color: colors["Overall %"],
      shape: "circle",
    };
  
    // If showMissing => define a "Missing Students" item in chunk 2
    const missingItem = showMissing
      ? {
          x: 2,
          label: "Enrollment Gap",
          color: "rgba(255,0,0,0.7)",
          shape: MissingStudentsLegendSymbol, // a function
        }
      : null;
  
    // Combine them in an array
    const legendItems = [CIPItem, SchoolItem];
    if (missingItem) legendItems.push(missingItem);
  
    // Now we actually render
    return (
      <g>
        {/* We'll map each item to a <g> inside the legend row */}
        {legendItems.map((item) => {
          // The chunk's left X
          const itemLeft = xOffset + item.x * chunkWidth;
          // For circles, let's place them at (0,0) => but we might offset
          // We'll place the text to the right
          // Tweak as desired
          return (
            <g key={item.label} transform={`translate(${itemLeft}, ${yOffset})`}>
              {/* Symbol */}
              {item.shape === "circle" ? (
                <circle cx={10} cy={10} r={8} fill={item.color} />
              ) : (
                // We assume item.shape is a function if not "circle"
                // e.g. MissingStudentsLegendSymbol
                <item.shape x={0} y={0} size={20} />
              )}
  
              {/* Legend Text */}
              <text
                x={28}
                y={14}
                className="text-s fill-gray-700"
                style={{
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                }}
              >
                {item.label}
              </text>
            </g>
          );
        })}
      </g>
    );
  };



// 5) A custom layer for your Tippy-based bar tooltips
const tooltipLayer = ({ bars }) => {
  return (
    <g>
      {bars.map((bar) => {
        // Determine if this bar is for CIP or Overall
        const isCIP = bar.data.id === 'CIP %';

        // Calculate the displayed ratio (e.g., 50 => 50%)
        const ratio = bar.data.value != null ? bar.data.value.toFixed(1) : null;

        // Skip filler rows or missing data
        if (ratio == null || bar.data.indexValue.startsWith('filler')) return null;

        // Pull numerator/denominator depending on CIP vs. Overall
        let numerator, denominator;
        if (isCIP) {
          numerator = bar.data.data.cipCount;
          denominator = bar.data.data.cipAll;
        } else {
          numerator = bar.data.data.schoolCount;
          denominator = bar.data.data.schoolAll;
        }

        // Dynamically set the tooltip border color to match the bar
        const borderColor = bar.color || 'rgba(0,0,0,0.15)';

        // Build your lines
        const labelLine = isCIP ? cipName : 'Overall Enrollment';
        const locationLine = selectedSchool?.label || selectedSchool?.value || '';

        // Construct the tooltip content
        const tooltipContent = (
          <div
            className="bg-white rounded-sm shadow-md p-2 text-sm text-gray-800"
            style={{
              border: `1px solid ${borderColor}`,
              maxWidth: 'calc(100vw - 20px)', // so it will shrink if near the edge
            }}
          >
            <div className="font-semibold text-gray-700 mb-1 text-balance">
              {labelLine}
            </div>
            <div className="mb-1">
              <strong>{ratio}%</strong> {bar.data.indexValue}{' '}
              <span className="text-gray-600">
                ({numerator?.toLocaleString()} of {denominator?.toLocaleString()})
              </span>
            </div>
            {locationLine && (
              <div className="text-xs text-gray-500">
                {locationLine}
              </div>
            )}
          </div>
        );


          return (
<Tippy
  key={bar.key}
  theme="clusterChartBlue"
  interactive={false}
  arrow={false}
  followCursor={true}
  content={tooltipContent}
  // “bottom-start” makes the tooltip appear below and left‐aligned,
  // then the offset [10,10] shifts it 10px right and 10px down from the cursor.
  placement="bottom-start"
  offset={[5, 5]}
  appendTo={document.body}
  plugins={[followCursor]}
  delay={[100, 0]}
  popperOptions={{
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          padding: 10,
        },
      },
      {
        name: 'flip',
        options: {
          // fallback placements if the tooltip would be clipped
          fallbackPlacements: ['bottom-end', 'right', 'left']
        },
      },
    ],
  }}
>
  <rect
    x={bar.x}
    y={bar.y}
    width={bar.width}
    height={bar.height}
    fill="transparent"
    className="cursor-pointer"
  />
</Tippy>
          );
        })}
      </g>
    );
  };



  return (
    <div className="flex flex-col items-center w-full max-w-6xl mx-auto p-4 box-border">


      <div className="w-full flex justify-end mt-4">
      <ExportPopover
          onCopyImage={copyImageToClipboard}
          onCopyData={copyDataToClipboard}
          onExportPNG={exportChartAsPNG}
          onExportExcel={exportChartToExcel}
        />
      </div>

      {cipEquityData && schoolDemo ? (
        hasChartData ? (
          <div ref={chartRef} className="w-full h-[520px] relative mt-4">
            <ResponsiveBar
              data={chartData}
              keys={keys}
              isInteractive={false}
              indexBy="studentGroup"
              layout="vertical"
              margin={{ top: 160, right: 0, bottom: 125, left: 80 }}
              padding={0.3}
              innerPadding={3}
              groupMode="grouped"
              colors={({ id }) => colors[id]}
              colorBy="id"
              borderWidth={2}
              borderRadius={2}
              borderColor={{
                from: 'color',
                modifiers: [['brighter', '0.3']],
              }}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                tickValues: chartData
                  .filter((d) => !d.studentGroup.startsWith('filler'))
                  .map((d) => d.studentGroup),
                renderTick: (props) => {
                  const { value, x, y } = props;
                  if (!value || value.startsWith('filler')) return null;
                  const lines = splitTextOnWords(value, 15);
                  return (
                    <g transform={`translate(${x},${y + 22})`}>
                      {lines.map((line, index) => (
                        <text
                          key={index}
                          x={0}
                          y={index * 15}
                          textAnchor="middle"
                          style={{
                            fill: '#333333',
                            fontSize: '0.875rem',
                            fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                          }}
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                },
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Percentage (%)',
                legendPosition: 'middle',
                legendOffset: -60,
                tickValues: 5,
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor="#000000"
              label={d => formatPercent(d.value)}
              
              theme={{
                fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                grid: {
                  line: {
                    stroke: '#a3a3a3',
                    strokeWidth: 1,
                    strokeDasharray: '6,4',
                  },
                },
                axis: {
                  domain: { line: { stroke: '#777777', strokeWidth: 1 } },
                  ticks: {
                    line: { stroke: '#777777', strokeWidth: 1 },
                    text: {
                      fill: '#333333',
                      fontSize: '0.875rem',
                      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                    },
                  },
                  legend: {
                    text: {
                      fill: '#333333',
                      fontSize: '1rem',
                      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                    },
                  },
                },
                legends: {
                  text: {
                    fill: '#333333',
                    fontSize: '0.875rem',
                    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                  },
                },
              }}
              defs={[
                {
                  id: 'missingHatch',
                  type: 'patternLines',
                  background: 'rgba(255,0,0,0.1)',
                  color: 'rgba(255,0,0,0.4)',
                  rotation: -45,
                  lineWidth: 4,
                  spacing: 8,
                },
              ]}
              fill={[
                {
                  match: { id: 'Missing' },
                  id: 'missingHatch',
                },
              ]}
              layers={[
                captureWidthLayer,
                // White background
                ({ width, height }) => (
                  <rect x={-100} y={0} width={width + 100} height={height} fill="#ffffff" />
                ),
                //  The custom layer for Title + Subtitle
({ width }) => {
  // 1) Prepare the text
  // We'll assume you've defined mainTitle and subTitle above.
  // We'll wrap the mainTitle if it’s too long, but subTitle is short enough for one line.

  const maxCharsPerLine = Math.floor(width / 12);
  const wrappedMainTitle = splitTextOnWords(titleText, maxCharsPerLine);

  // Decide font sizes
  // If the mainTitle has >2 lines, we scale down the font
  const mainFontSize = wrappedMainTitle.length > 2 ? "1.05rem" : "1.25rem";
  const subFontSize = wrappedMainTitle.length > 2 ? "0.68rem" : ".89rem";

  // 2) Positioning
  // We'll place the background rect up top.
  //   yRect: how far above the chart
  //   hRect: a guess for how tall it must be
  const yRect = -165;
  const hRect = 220; // enough space for 2 lines + some buffer

  // For the main text, we place it about 1/3 down from that rect, then the subtitle below it
  const mainTextY = -120;
  // Then the subtitle we place ~20 px below main text
  const subTextY = wrappedMainTitle.length > 2 ? -60 : -70;

  // 3) Return the actual <g> to render
  return (
    <g>
      {/* White background behind the two lines */}
      <rect x={-100} y={yRect} width={width + 100} height={hRect} fill="#ffffff" />

      {/* MAIN TITLE - multiline */}
      <text
        x={width / 2 - 100}
        y={mainTextY}
        textAnchor="middle"
        style={{
          fontSize: mainFontSize,
          fontWeight: "bold",
          fill: "#333333",
          fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        }}
      >
        {wrappedMainTitle.map((line, i) => (
          <tspan key={i} x={width / 2 - 100} dy={i === 0 ? 0 : "1.2em"}>
            {line}
          </tspan>
        ))}
      </text>

      {/* SUBTITLE - single line, smaller */}
      <text
        x={width / 2 - 100}
        y={subTextY}
        textAnchor="middle"
        style={{
          fontSize: subFontSize,
          fontWeight: "normal",
          fill: "#555555",
          fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        }}
      >
        {subTitle}
      </text>
    </g>
  );
},
                // Custom Grid Lines
                ({ yScale, width }) => {
                  const ticks = yScale.ticks ? yScale.ticks(5) : yScale.domain();
                  return (
                    <g>
                      {ticks.map((tick, i) => (
                        <line
                          key={i}
                          x1={0}
                          x2={width}
                          y1={yScale(tick)}
                          y2={yScale(tick)}
                          stroke="#a3a3a3"
                          strokeWidth={0.5}
                          strokeDasharray="6,4"
                        />
                      ))}
                    </g>
                  );
                },
                'bars',
                'axes',
                'markers',
                'legends',
                
                // Caption Area for suppressed groups
                ({ width, height }) => {
                  if (suppressedGroups.length === 0) return null;
                  const startX = 0;
                  const startY = height - 218;
                  const fontSize = '.75rem';
                  const lineHeight = 14;
                  return (
                    <g transform={`translate(${startX}, ${startY})`}>
                      <text
                        x={0}
                        y={0}
                        textAnchor="start"
                        style={{
                          fontSize: fontSize,
                          fill: '#71717a',
                          fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                          fontStyle: 'italic',
                        }}
                      >
                        {'The following groups were not displayed due to low percentages:'}
                      </text>
                      {suppressedGroups.map((groupData, index) => {
                        const lineY = (index + 1) * lineHeight;
                        const cipPercentageText =
                          groupData['CIP %'] !== null
                            ? `${groupData['CIP %'].toFixed(1)}%`
                            : 'N/A';
                        const schoolPercentageText =
                          groupData['Overall %'] !== null
                            ? `${groupData['Overall %'].toFixed(1)}%`
                            : 'N/A';
                        return (
                          <text
                            key={index}
                            x={0}
                            y={lineY}
                            textAnchor="start"
                            style={{
                              fontSize: fontSize,
                              fill: '#71717a',
                              fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
                              fontStyle: 'italic',
                            }}
                          >
                            {`${groupData.studentGroup}: CIP ${cipPercentageText}, School ${schoolPercentageText}`}
                          </text>
                        );
                      })}
                    </g>
                  );
                },
               
// Single Combined Layer for Missing Students
({ bars }) => {
  if (!showMissing) return null;
   return (
    <g>
      {bars
        .filter((bar) => bar.data.id === "CIP %")
        .map((cipBar) => {
          // CIP data
          const cipData = cipBar.data.data;
          let cipPct = cipData["CIP %"];
          const schPct = cipData["Overall %"];
          const cipAll = cipData.cipAll;
          const studentGroup = cipData.studentGroup; 
           // Get the display name
  const displayGroupName = getStudentGroupDisplay(studentGroup);

          // Find matching School bar
          const schoolBar = bars.find(
            (b) =>
              b.data.indexValue === cipBar.data.indexValue &&
              b.data.id === "Overall %"
          );

          // If CIP is null but total>0, treat as 0
          if (cipPct == null && schPct != null && cipAll > 0) {
            cipPct = 0;
          }

          // If CIP >= School or missing, skip
          if (!schoolBar || cipPct == null || schPct == null || cipAll == null) return null;
          if (cipPct >= schPct) return null;

          // Calculate missing
          const missingHeight = cipBar.y - schoolBar.y;
          if (missingHeight <= 0) return null;

          const missingRatio = (schPct - cipPct) / 100;
          const missingStudents = Math.round(missingRatio * cipAll);
          if (missingStudents <= 0) return null;

          // For the small label
          const labelWidth = 50;
          const labelHeight = 16;
          const iconSize = 10;
          const spacing = 4;

          // Top offset for label: 22 px above dashed rectangle
          const yTop = schoolBar.y - 22;
          // X center for label
          const xCenter = cipBar.x + cipBar.width / 2;

          // We'll define a large invisible rect covering from the label (yTop) 
          // down to the CIP bar's top (cipBar.y).
          const bigRectHeight = (cipBar.y - yTop);
          if (bigRectHeight <= 0) return null; // Just in case

          // Tippy content
          const tooltipContent = (
            <div
      className="bg-white rounded-sm shadow-md p-2 text-sm text-gray-800 border-red-200 border "
    >
      <div className="font-semibold mb-1 w-full text-red-400">Enrollment Gap</div>
      <div>
        <strong>CIP:</strong> {cipPct.toFixed(1)}% vs.
        <strong> School:</strong> {schPct.toFixed(1)}%
      </div>
      <div className="mt-1">
        <strong>{missingStudents}</strong> more {displayGroupName} needed 
        in {cipName} to match {schOrDist}'s representation.
      </div>
    </div>
          );

          return (
            <Tippy
              key={`missing-combined-${cipBar.key}`}
              theme='missingStudents'
              content={tooltipContent}
              placement="bottom-start"
              offset={[5, 5]}
              arrow={false}
              interactive={false}
              appendTo={document.body}
              plugins={[followCursor]}
              followCursor={true}
              delay={[100, 0]}
              popperOptions={{
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      padding: 10,
                    },
                  },
                  {
                    name: 'flip',
                    options: {
                      // fallback placements if the tooltip would be clipped
                      fallbackPlacements: ['bottom-end', 'right', 'left']
                    },
                  },
                ],
              }}

            >
          
              <g className="cursor-pointer">
                {/* 1) Big invisible rect = entire “hover area.” */}
                <rect
                  x={cipBar.x}
                  y={yTop}
                  width={cipBar.width}
                  height={bigRectHeight}
                  fill="transparent"
                />

                {/* 2) Dashed rectangle from School bar to CIP bar */}
                <rect
                  x={cipBar.x}
                  y={schoolBar.y}
                  width={cipBar.width}
                  height={missingHeight}
                  fill="none"
                  stroke="rgba(255,0,0,0.6)"
                  strokeDasharray="5 4"
                  strokeWidth={2}
                  strokeLinecap="round"
                />

                {/* 3) The small white label behind “x missing” */}
                <rect
                  x={xCenter - labelWidth / 2}
                  y={yTop}
                  width={labelWidth}
                  height={labelHeight}
                  fill="#ffffff"
                />

                {/* 4) Person icon + “x missing” text */}
                <g
                  transform={`translate(${xCenter - (iconSize + spacing + 20) / 2}, ${
                    yTop + labelHeight / 2
                  })`}
                >
                  <g transform="translate(0, -5) scale(0.5)">
                    <path
                      fill="rgba(255,0,0,0.7)"
                      d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
                    />
                  </g>
                  <text
                    x={iconSize + spacing}
                    y={3}
                    textAnchor="start"
                    style={{
                      fill: "rgba(255,0,0,0.7)",
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                    }}
                  >
                    x {missingStudents}
                  </text>
                </g>
              </g>
            </Tippy>
          );
        })}
    </g>
  );
},tooltipLayer,(props) => dynamicLegendLayer({
  ...props,              // pass (width, height, etc.)
  showMissing,
  cipDoc,
  districtName,
  selectedSchool,
  MissingStudentsLegendSymbol,
  colors
}),
                           
         
              ]}
            />
          </div>
        ) : (
          <div className="text-center mt-4">
            {`No enrollment data for ${selectedSchool.value} in ${selectedFilterIndicatorDate.label}.`}
          </div>
        )
      ) : (
        <div className="text-center mt-4">Loading...</div>
      )}
    </div>
  );
};

export default EquityNivoBarChart;